/* eslint-disable jira/filename-case */

/**
 * Boundary tracking data exposed for composition with other trackers.
 */
export type BoundaryData = {
	/** Set of boundary markers whose content was removed during hydration */
	failedBoundaryMarkers: Set<Node>;
	/** Maps a boundary marker to its nested child boundary markers */
	nestedMarkersByMarker: Map<Node, Node[]>;
	/** Maps a boundary marker to the DOM parent captured before hydration */
	capturedParentByMarker: Map<Node, Node | null>;
	/** Maps a boundary marker to its sibling index within the parent's childNodes captured before hydration */
	capturedSiblingIndexByMarker: Map<Node, number>;
	/** Maps a failed boundary marker to its original top-level element children (now detached, still readable) */
	capturedTopLevelChildrenByMarker: Map<Node, Node[]>;
	/** Maps a failed boundary marker to the slot index of its first child in the parent's filtered element children */
	capturedSlotIndexByMarker: Map<Node, number>;
};

export type CaptureDocumentStateOptions = {
	captureBoundaryData?: boolean;
};

export type HydrationOutcomeStats = {
	/**
	 * the original dom count
	 */
	original: number;
	/**
	 * the number of nodes that were preserved during hydration
	 */
	preserved: number;
	/**
	 * the number of nodes that were added since capture
	 */
	added: number;
	/**
	 * the number of nodes that were removed likely due to hydration mismatch
	 */
	removed: number;
	/**
	 * the number of nodes that were removed for "other reasons" (not due to hydration mistmatch)
	 */
	unmounted: number;
	/**
	 * the number of failed suspense boundaries detected
	 */
	failedBoundaries: number;

	preservationRatio: number;

	removedNodes: Map<Node, Node>;
	unmountedNodes: Map<Node, Node>;
	boundaryData?: BoundaryData;
};

export type HydrationOutcomeReporter = () => HydrationOutcomeStats;

const allRemoved = (nodes: Node[]): boolean => nodes.every((n) => !n.parentNode);

const buildChildrenByParent = (
	nodes: Node[],
	parents: Map<Node, Node | null>,
): Map<Node, Node[]> => {
	const childrenByParent = new Map<Node, Node[]>();
	for (const node of nodes) {
		const parent = parents.get(node) ?? null;
		if (parent) {
			const existingChildren = childrenByParent.get(parent);
			if (existingChildren) {
				existingChildren.push(node);
			} else {
				childrenByParent.set(parent, [node]);
			}
		}
	}
	return childrenByParent;
};

export function captureDocumentState(
	container: Node,
	{ captureBoundaryData = false }: CaptureDocumentStateOptions = {},
): HydrationOutcomeReporter {
	const originalParents = new Map<Node, Node | null>();
	const nodeToBoundary = new Map<Node, Node>();
	const boundaries = new Map<Node, Node[]>();
	const nestedMarkersByMarker = captureBoundaryData ? new Map<Node, Node[]>() : undefined;
	const capturedParentByMarker = captureBoundaryData ? new Map<Node, Node | null>() : undefined;
	const capturedSiblingIndexByMarker = captureBoundaryData ? new Map<Node, number>() : undefined;

	function collectAllNodes(root: Node, captureOriginalState = false): Node[] {
		const out: Node[] = [];
		const documentNode =
			// oxlint-disable-next-line typescript/consistent-type-assertions
			root.nodeType === Node.DOCUMENT_NODE ? (root as Document) : root.ownerDocument;
		if (!documentNode) {
			return out;
		}
		const walker = documentNode.createTreeWalker(root, NodeFilter.SHOW_ALL);
		const stack: string[] = [];
		const boundaryTracker: Node[] = [];

		while (walker.nextNode()) {
			const node = walker.currentNode;

			// special handling for comments to detect start and end of failed suspense blocks
			if (node.nodeType === Node.COMMENT_NODE) {
				const commentContent = node.nodeValue?.trim() ?? '';
				// Detect start of suspense block
				if (commentContent === '$!' || commentContent === '$') {
					stack.push(commentContent);

					if (captureOriginalState) {
						boundaries.set(node, []);
						if (
							captureBoundaryData &&
							nestedMarkersByMarker &&
							capturedParentByMarker &&
							capturedSiblingIndexByMarker
						) {
							const outerMarker =
								boundaryTracker.length > 0 ? boundaryTracker[boundaryTracker.length - 1] : null;
							nestedMarkersByMarker.set(node, []);
							if (outerMarker) {
								nestedMarkersByMarker.get(outerMarker)?.push(node);
							}

							capturedParentByMarker.set(node, node.parentNode);
							if (node.parentNode) {
								capturedSiblingIndexByMarker.set(
									node,
									Array.from(node.parentNode.childNodes).findIndex(
										(childNode) => childNode === node,
									),
								);
							}
						}
					}

					boundaryTracker.push(node);
				}

				// Detect end of suspense block
				if (commentContent === '/$') {
					stack.pop();
					boundaryTracker.pop();
				}
				// always jump over any comments
				// oxlint-disable-next-line no-continue
				continue;
			}

			if (stack.includes('$!')) {
				// we are inside a failed suspense block, skip everything until we reach the end
				// these nodes will be removed by React anyway and we dont want to "focus" on here
				// the goal is to support "normal" hydration preservation
				// oxlint-disable-next-line no-continue
				continue;
			}

			// ignore "text" nodes
			if (node.nodeType === Node.TEXT_NODE) {
				// oxlint-disable-next-line no-continue
				continue;
			}

			// ignore "template" nodes (part of failed suspense and testing harness)
			if (node instanceof HTMLTemplateElement) {
				// oxlint-disable-next-line no-continue
				continue;
			}

			out.push(node);
			if (captureOriginalState) {
				originalParents.set(node, node.parentNode);

				// connect node to the current "boundary"
				const currentBoundary = boundaryTracker[boundaryTracker.length - 1];
				if (currentBoundary) {
					nodeToBoundary.set(node, currentBoundary);
					if (node.parentNode === currentBoundary.parentNode) {
						boundaries.get(currentBoundary)?.push(node);
					}
				}
			}
		}
		if (stack.length > 0) {
			throw new Error('Mismatched suspense comments, found more start than end');
		}
		return out;
	}

	// Capture pre-hydration snapshot
	const originalNodes = collectAllNodes(container, true);
	const originalSet = new Set(originalNodes);
	const originalChildrenByParent = captureBoundaryData
		? buildChildrenByParent(originalNodes, originalParents)
		: undefined;

	let lastResult: HydrationOutcomeStats | undefined;
	return () => {
		// snapshots will be erased after run to prevent memory leaks
		// store and return lastResult
		if (lastResult) {
			return lastResult;
		}
		const currentNodes = collectAllNodes(container);
		const currentSet = new Set(currentNodes);

		const removedNodes: Node[] = [];
		const unmountedNodes: Node[] = [];
		const failedBoundaryMarkers = new Set<Node>();

		for (const n of originalSet) {
			if (!currentSet.has(n)) {
				// check if this node was unmounted as part of a failed suspense boundary
				const boundaryId = nodeToBoundary.get(n);
				const boundaryElements = boundaryId && boundaries.get(boundaryId);
				if (boundaryId && (!boundaryElements || allRemoved(boundaryElements))) {
					// it was removed as part of hydration mismatch
					removedNodes.push(n);
					failedBoundaryMarkers.add(boundaryId);
				} else {
					// it was preserved during hydration and removed later for some other reason.
					unmountedNodes.push(n);
				}
			}
		}

		const addedNodes: Node[] = [];
		for (const n of currentSet) {
			if (!originalSet.has(n)) {
				addedNodes.push(n);
			}
		}

		const findPreservedParents = (affectedNodes: Node[]): Map<Node, Node> =>
			new Map(
				affectedNodes.flatMap<[Node, Node]>((node) => {
					const parent = originalParents.get(node) ?? null;
					if (!parent || !container.contains(parent)) {
						// we do not report any "inner" node focusing on the "edge"
						return [];
					}
					return [[node, parent]];
				}),
			);

		const topLevelRemovedNodes = findPreservedParents(removedNodes);
		const topLevelUnmountedNodes = findPreservedParents(unmountedNodes);

		const original = originalSet.size;
		const removed = removedNodes.length;
		const unmounted = unmountedNodes.length;
		const added = addedNodes.length;
		const preserved = original - removed;

		const boundaryData =
			captureBoundaryData &&
			nestedMarkersByMarker &&
			capturedParentByMarker &&
			capturedSiblingIndexByMarker &&
			originalChildrenByParent
				? {
						failedBoundaryMarkers,
						nestedMarkersByMarker,
						capturedParentByMarker,
						capturedSiblingIndexByMarker,
						capturedTopLevelChildrenByMarker: new Map(
							[...failedBoundaryMarkers].map<[Node, Node[]]>((marker) => [
								marker,
								boundaries.get(marker) ?? [],
							]),
						),
						capturedSlotIndexByMarker: new Map(
							[...failedBoundaryMarkers].map<[Node, number]>((marker) => {
								const origChildren = boundaries.get(marker) ?? [];
								const firstOriginalNode = origChildren[0] ?? null;
								if (!firstOriginalNode) {
									return [marker, -1];
								}
								const parent = capturedParentByMarker.get(marker) ?? null;
								if (!parent) {
									return [marker, -1];
								}
								const parentOrigChildren = originalChildrenByParent.get(parent) ?? [];
								return [marker, parentOrigChildren.indexOf(firstOriginalNode)];
							}),
						),
					}
				: undefined;

		// clear transient snapshot state
		originalParents.clear();
		nodeToBoundary.clear();
		boundaries.clear();
		originalChildrenByParent?.clear();
		originalNodes.length = 0;
		originalSet.clear();

		lastResult = {
			original,
			preserved,
			removed,
			unmounted,
			added,
			failedBoundaries: failedBoundaryMarkers.size,
			preservationRatio: preserved / original,
			removedNodes: topLevelRemovedNodes,
			unmountedNodes: topLevelUnmountedNodes,
			boundaryData,
		};
		return lastResult;
	};
}
